import { sort } from "./utils";

/**
 * Sentinel ID used for the "No objective" virtual entry in the goals tree.
 * When a goal has at least one measure without an objective, this entry is added
 * to that goal's objectives list. Selecting it filters to measures with the goal
 * but no objective assigned.
 */
export const NO_OBJECTIVE_ID = "__no_objective__";

/**
 * Converts a flat measures array into the goals array format expected by GoalObjectiveTreeSelect.
 * This utility is called by the parent component (AuthoringAndReview), NOT by GoalObjectiveTreeSelect itself.
 *
 * @param {Array} measures - Array of { goal: { id, description }, objective: { id, description } | null }
 * @returns {Array} Goals array: [{ id, description, objectives: [{ id, description }] }]
 *
 * Rules:
 * - Groups measures by goal.id to form goal entries
 * - Each unique objective.id under a goal becomes an objective entry
 * - If a goal has at least one measure without an objective, a "No objective" entry is added
 * - Sorts goals alphabetically by sort key `description || id` (falls back to id when description is empty)
 * - Sorts objectives alphabetically by sort key `description || id` within each goal
 * - Stores the source `description` field as-is; if empty, undefined, or null, stores empty string ""
 * - Deduplicates goals and objectives by id
 * - Measures with goal: null are excluded
 */
export function buildGoalTreeFromMeasures(measures) {
  if (!measures || measures.length === 0) {
    return [];
  }

  // Map of goal.id -> { id, description, objectivesMap, hasNoObjective }
  const goalsMap = new Map();

  for (const measure of measures) {
    if (!measure.goal) {
      continue;
    }

    const goalId = measure.goal.id;
    const goalDescription = measure.goal.description || "";

    if (!goalsMap.has(goalId)) {
      goalsMap.set(goalId, {
        id: goalId,
        description: goalDescription,
        objectivesMap: new Map(),
        hasNoObjective: false,
      });
    }

    const goalEntry = goalsMap.get(goalId);

    if (measure.objective && measure.objective.id != null) {
      const objId = measure.objective.id;
      if (!goalEntry.objectivesMap.has(objId)) {
        goalEntry.objectivesMap.set(objId, {
          id: objId,
          description: measure.objective.description || "",
        });
      }
    } else {
      goalEntry.hasNoObjective = true;
    }
  }

  if (goalsMap.size === 0) {
    return [];
  }

  // Convert map to sorted array
  const sortKey = (item) => item.description || item.id;

  const goals = Array.from(goalsMap.values()).map((entry) => {
    const objectives = sort(Array.from(entry.objectivesMap.values()), sortKey);
    // Add "No objective" entry at the end if applicable
    if (entry.hasNoObjective) {
      objectives.push({
        id: `${entry.id}:${NO_OBJECTIVE_ID}`,
        description: "No objective",
      });
    }
    return { id: entry.id, description: entry.description, objectives };
  });

  return sort(goals, sortKey);
}
